import React, { useState } from 'react';
import { FiChevronRight, FiCheck, FiDownload, FiPhone, FiMail, FiArrowLeft } from 'react-icons/fi';

const ProductDetail = ({ product, onBack }) => {
  const [activeTab, setActiveTab] = useState('description');
  const [activeImage, setActiveImage] = useState(0);

  // Default product data if none provided
  const defaultProduct = {
    id: 1,
    name: 'AIS Panel 24kV',
    category: 'Air Insulated Switchgear',
    voltage: '24kV',
    current: '1250A',
    description: 'Panel switchgear medium voltage dengan isolasi udara yang handal dan efisien untuk berbagai aplikasi industri dan komersial.',
    price: 'Request Quote',
    images: ['Main View', 'Side View', 'Internal View', 'Detail View'],
    features: [
      'Isolasi udara berkualitas tinggi',
      'Circuit breaker vacuum',
      'Sistem interlocking mekanis dan elektrik',
      'Kompartemen yang dapat dipisah',
      'Arc resistance tested',
      'Easy maintenance access'
    ],
    specifications: {
      'Rated Voltage': '24kV',
      'Rated Current': '1250A',
      'Short-circuit Current': '25kA/3s',
      'Frequency': '50/60Hz',
      'Insulation Level': 'BIL 125kV',
      'Protection Class': 'IP4X',
      'Operating Temperature': '-5°C to +40°C',
      'Standards': 'IEC 62271-200'
    },
    applications: [
      'Power Distribution Substations',
      'Industrial Plants',
      'Commercial Buildings',
      'Mining Operations',
      'Renewable Energy Projects',
      'Infrastructure Projects'
    ]
  };

  const currentProduct = product || defaultProduct;

  return (
    <div className="product-detail-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <div className="page-header-content">
            <span className="page-subtitle">{currentProduct.category}</span>
            <h1 className="page-title">{currentProduct.name}</h1>
            <div className="breadcrumb">
              <a href="#home">Home</a>
              <FiChevronRight />
              <a href="#products" onClick={(e) => { e.preventDefault(); onBack && onBack(); }}>Products</a>
              <FiChevronRight />
              <span>{currentProduct.name}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Product Detail Section */}
      <section className="product-detail-section section">
        <div className="container">
          {/* Back Button */}
          <button className="back-button" onClick={onBack}>
            <FiArrowLeft /> Back to Products
          </button>

          <div className="product-detail-grid">
            {/* Product Images */}
            <div className="product-gallery">
              <div className="main-image">
                <div className="main-image-placeholder">
                  {currentProduct.images ? currentProduct.images[activeImage] : currentProduct.image}
                </div>
              </div>
              <div className="thumbnail-grid">
                {(currentProduct.images || ['Main View', 'Side View', 'Internal View', 'Detail View']).map((img, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${activeImage === index ? 'active' : ''}`}
                    onClick={() => setActiveImage(index)}
                  >
                    <div className="thumbnail-placeholder">{img}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="product-detail-info">
              <span className="product-category-badge">{currentProduct.category}</span>
              <h1 className="product-detail-title">{currentProduct.name}</h1>

              <div className="product-quick-specs">
                <div className="quick-spec">
                  <span className="spec-label">Voltage</span>
                  <span className="spec-value">{currentProduct.voltage}</span>
                </div>
                <div className="quick-spec">
                  <span className="spec-label">Current</span>
                  <span className="spec-value">{currentProduct.current}</span>
                </div>
              </div>

              <p className="product-detail-description">
                {currentProduct.description}
              </p>

              <div className="product-features-list">
                <h4>Key Features:</h4>
                <ul>
                  {(currentProduct.features || defaultProduct.features).map((feature, index) => (
                    <li key={index}>
                      <FiCheck className="feature-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="product-actions">
                <a href="#contact" className="btn btn-primary btn-lg">
                  Request Quote
                </a>
                <button className="btn btn-outline-dark">
                  <FiDownload /> Download Datasheet
                </button>
              </div>

              <div className="product-contact-box">
                <h4>Need Assistance?</h4>
                <div className="contact-options">
                  <a href="tel:+622112345678" className="contact-option">
                    <FiPhone />
                    <span>+62-21-1234567</span>
                  </a>
                  <a href="mailto:sales@shuanghuipower.co.id" className="contact-option">
                    <FiMail />
                    <span>sales@shuanghuipower.co.id</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Product Tabs */}
          <div className="product-tabs">
            <div className="tabs-header">
              <button
                className={activeTab === 'description' ? 'active' : ''}
                onClick={() => setActiveTab('description')}
              >
                Description
              </button>
              <button
                className={activeTab === 'specifications' ? 'active' : ''}
                onClick={() => setActiveTab('specifications')}
              >
                Specifications
              </button>
              <button
                className={activeTab === 'applications' ? 'active' : ''}
                onClick={() => setActiveTab('applications')}
              >
                Applications
              </button>
            </div>

            <div className="tabs-content">
              {activeTab === 'description' && (
                <div className="tab-pane">
                  <h3>Product Description</h3>
                  <p>
                    {currentProduct.name} adalah switchgear medium voltage berkualitas tinggi yang
                    dirancang untuk memenuhi kebutuhan distribusi daya di berbagai sektor industri.
                    Produk ini menggunakan teknologi terkini dengan standar internasional IEC untuk
                    memastikan keandalan dan keamanan operasional.
                  </p>
                  <p>
                    Dengan desain yang compact namun robust, switchgear ini menawarkan kemudahan
                    instalasi dan maintenance. Sistem proteksi yang terintegrasi memberikan
                    perlindungan maksimal terhadap gangguan listrik seperti short circuit,
                    overload, dan earth fault.
                  </p>
                  <h4>Advantages</h4>
                  <ul>
                    <li>Desain compact menghemat ruang instalasi</li>
                    <li>Maintenance yang mudah dan cepat</li>
                    <li>Sistem interlocking untuk keamanan operator</li>
                    <li>Tahan terhadap kondisi lingkungan yang ekstrem</li>
                    <li>Garansi dan dukungan teknis profesional</li>
                  </ul>
                </div>
              )}

              {activeTab === 'specifications' && (
                <div className="tab-pane">
                  <h3>Technical Specifications</h3>
                  <table className="specs-table">
                    <tbody>
                      {Object.entries(currentProduct.specifications || defaultProduct.specifications).map(([key, value]) => (
                        <tr key={key}>
                          <td className="spec-key">{key}</td>
                          <td className="spec-val">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'applications' && (
                <div className="tab-pane">
                  <h3>Application Areas</h3>
                  <p>
                    Produk ini cocok untuk berbagai aplikasi distribusi daya medium voltage,
                    termasuk namun tidak terbatas pada:
                  </p>
                  <div className="applications-grid">
                    {(currentProduct.applications || defaultProduct.applications).map((app, index) => (
                      <div className="application-item" key={index}>
                        <FiCheck className="app-icon" />
                        <span>{app}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
